// pages/Campaigns/Gmail/PromptSettings.js
import React, { useState, useEffect } from 'react';
import axiosInstance from "../../../utils/axiosInstance";
import backendURL from '../../../config';

export default function PromptSettings({ chatbotId, permission }) {
    const [prompt, setPrompt] = useState('');
    const [isAutoReplyEnabled, setIsAutoReplyEnabled] = useState(false);
    const [isLoading, setIsLoading] = useState(true);
    const [isSaving, setIsSaving] = useState(false);
    const [error, setError] = useState('');
    const [successMessage, setSuccessMessage] = useState('');
    const params = chatbotId ? { chatbot_id: chatbotId } : {};

    useEffect(() => {
        const fetchSettings = async () => {
            setIsLoading(true);
            setError('');
            setSuccessMessage('');
            try {
                const response = await axiosInstance.get(`/api/gmail/settings`, {
                    params
                });
                setPrompt(response.data.prompt);
                setIsAutoReplyEnabled(response.data.is_autoreply_enabled);
            } catch (err) {
                setError(err.response?.data?.message || "Failed to load settings.");
            } finally {
                setIsLoading(false);
            }
        };
        fetchSettings();
    }, []);

    const handleSave = async () => {
        setIsSaving(true);
        setError('');
        setSuccessMessage('');
        try {
            const response = await axiosInstance.post(`/api/gmail/settings`, 
                { prompt, is_autoreply_enabled: isAutoReplyEnabled }, 
                { params }
            );
            setSuccessMessage(response.data.message);
        } catch (err) {
            setError(err.response?.data?.message || "Failed to save settings.");
        } finally {
            setIsSaving(false);
        }
    };

    if (isLoading) {
        return <p>Loading settings...</p>;
    }

    return (
        <div style={{ flexGrow: 1, display: 'flex', flexDirection: 'column', overflowY: 'auto' }}>
            {error && <div className="alert alert-danger">{error}</div>}
            {successMessage && <div className="alert alert-success">{successMessage}</div>}

            <div className="mb-3">
                <label htmlFor="aiPrompt" className="form-label"><strong>AI Prompt Template</strong></label>
                <textarea
                    id="aiPrompt"
                    className="form-control"
                    rows="15"
                    value={prompt}
                    onChange={(e) => setPrompt(e.target.value)}
                    disabled={isSaving || !permission.canModify}
                ></textarea>
                <small className="form-text text-muted">
                    Edit the prompt used by the AI to generate replies. Use placeholders like <code>{'{sender_name}'}</code> and <code>{'{email_body}'}</code>.
                </small>
            </div>

            <div className="form-check form-switch mb-5">
                <input
                    className="form-check-input"
                    type="checkbox"
                    role="switch"
                    id="autoReplyToggle"
                    checked={isAutoReplyEnabled}
                    onChange={() => setIsAutoReplyEnabled(!isAutoReplyEnabled)}
                    disabled={isSaving || !permission.canModify}
                />
                <label className="form-check-label" htmlFor="autoReplyToggle">
                    <strong>Enable Automated AI Replies</strong>
                </label>
                <small className="form-text text-muted d-block">
                    When enabled, the AI will automatically send the generated reply without manual approval. (This feature is currently in development).
                </small>
            </div>

            {permission.canModify && (
                <button className="btn btn-primary" onClick={handleSave} disabled={isSaving}>
                    {isSaving ? 'Saving...' : 'Save Settings'}
                </button>
            )}
        </div>
    );
}